import { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'
import Data from "../data/Data";
import Header from './Header';

function SignUp() {

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [country, setCountry] = useState('');
  const [type, setType] = useState('');
  const [birthday, setBirthday] = useState('');
  const [countries, setCountries] = useState([]);
  const [validated, setValidated] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef();
  const emailInputRef = useRef();
  const passwordInputRef = useRef();
  const passwordAgainInputRef = useRef();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    //if logged in, redirect to dashboard
    if (isLoggedIn)
      navigate('/dashboard');

    const fetchCountries = async () => {
      setCountries(await Data.fetchCountries());
    };

    fetchCountries();
  }, [isLoggedIn, navigate])

  const clearError = () => setErrorMessage('');

  const handleInputChange = (e, ref) => {
    ref.current.setCustomValidity('');
    if (e.target.name === 'email') {
      setEmail(e.target.value);
    } else if (e.target.name === 'password') {
      if (passwordAgain && e.target.value !== passwordAgain) {
        passwordAgainInputRef.current.setCustomValidity('Passwords do not match');
      } else {
        passwordAgainInputRef.current.setCustomValidity('');
      }
      setPassword(e.target.value);
    } else if (e.target.name === 'passwordAgain') {
      if (e.target.value !== password) {
        passwordAgainInputRef.current.setCustomValidity('Passwords do not match');
      } else {
        passwordAgainInputRef.current.setCustomValidity('');
      }
      setPasswordAgain(e.target.value);
    }

    clearError();
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (formRef.current.checkValidity() === false) {
      e.stopPropagation();
    } else {
      const response = await Data.fetchSignUp(firstName, lastName, email, password, passwordAgain, country, type, birthday);

      if (response) {
        // handle server-side error
        if (response === 'INVALID_PASSWORD') {
          const message = 'Password is invalid. It must contain at least one lowercase letter, one uppercase letter, and one digit.';
          setErrorMessage(message);
          passwordInputRef.current.setCustomValidity(message);
        }
        else if (response === 'PASSWORD_MISMATCH') {
          const message = 'Passwords do not match';
          setErrorMessage(message);
          passwordInputRef.current.setCustomValidity(message);
        }
        else if (response.errors?.length > 0) {
          const emailError = response.errors.find(error => error.field === 'EMAIL' && error.code === 'NOT_UNIQUE');
          if (emailError) {
            const message = 'Email already exists';
            setErrorMessage(message);
            emailInputRef.current.setCustomValidity(message);
          }
        }
        else {
          const message = 'Something went wrong';
          setErrorMessage(message);
          passwordInputRef.current.setCustomValidity(message);
          emailInputRef.current.setCustomValidity(message);
        }
      }
      else {
        // successful message
        window.location.href = '/login';
      }
    }

    setPassword('');
    setPasswordAgain('');
    setValidated(true);
  }

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div>
      <Header whiteLogo />
      <div className="Login py-5 container-fluid d-flex justify-content-center align-items-center">
        <div className="col-12 col-lg-6 box pb-5">
          <h1 className="mb-4">Sign Up</h1>
          {errorMessage && !errorMessage.includes('Password') && !errorMessage.includes('Email') &&
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {errorMessage}
              <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close" onClick={clearError}></button>
            </div>
          }
          <form ref={formRef} className={`needs-validation ${validated ? 'was-validated' : ''}`} noValidate onSubmit={handleSignUp}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="inputFirstName" className="form-label">First name</label>
                <input required type="text" className="form-control" id="inputFirstName" name="firstName" value={firstName} onChange={e => setFirstName(e.target.value)} />
                <div className="invalid-feedback">First name is required</div>
              </div>
              <div className="col-md-6">
                <label htmlFor="inputLastName" className="form-label">Last name</label>
                <input required type="text" className="form-control" id="inputLastName" name="lastName" value={lastName} onChange={e => setLastName(e.target.value)} />
                <div className="invalid-feedback">Last name is required</div>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="inputBirthday" className="form-label">Birthday</label>
              <input required type="date" max={getCurrentDate()} className="form-control" id="inputBirthday" name="birthday" value={birthday} onChange={e => setBirthday(e.target.value)} />
              <div className="invalid-feedback">Birthday is required</div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="inputCountry" className="form-label">Country</label>
                {/* <input required type="text" className="form-control" id="inputCountry" name="country" value={country} onChange={e => setCountry(e.target.value)} /> */}
                <select required className="form-control custom-select" id="inputCountry" name="country" value={country} onChange={e => setCountry(e.target.value)} >
                  <option value="" disabled>Select a country</option>
                  {countries.map(country => (
                    <option key={country.cca2} value={country.cca2}>
                      {country.name.common}
                    </option>
                  ))}
                </select>
                <div className="invalid-feedback">Country is required</div>
              </div>
              <div className="col-md-6">
                <label htmlFor="inputType" className="form-label">Type</label>
                {/* <input required type="text" className="form-control" id="inputType" name="type" value={type} onChange={e => setType(e.target.value)} /> */}
                <select required className="form-control custom-select" id="inputType" name="type" value={type} onChange={e => setType(e.target.value)}>
                  <option value="" disabled>Select a type</option>
                  <option value="designer">Designer</option>
                  <option value="customer">Customer</option>
                </select>
                <div className="invalid-feedback">Type is required</div>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="inputEmail" className="form-label">Email</label>
              <input ref={emailInputRef} required type="email" pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$" className="form-control" id="inputEmail" name="email" value={email} onChange={e => handleInputChange(e, emailInputRef)} />
              <div className="invalid-feedback">
                {emailInputRef.current && emailInputRef.current.validationMessage !== 'Something went wrong' ? (emailInputRef.current.validationMessage.includes('Email') ? emailInputRef.current.validationMessage : 'Please enter a valid email address') : ''}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="inputPassword" className="form-label">Password</label>
                <input ref={passwordInputRef} type="password" name="password" className="form-control" id="password" required value={password} onChange={e => handleInputChange(e, passwordInputRef)} />
                <div className="invalid-feedback">
                  {passwordInputRef.current && passwordInputRef.current.validationMessage !== 'Something went wrong' ? (passwordInputRef.current.validationMessage.includes('Password') ? passwordInputRef.current.validationMessage : 'Password is required') : ''}
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="inputPasswordAgain" className="form-label">Password again</label>
                <input ref={passwordAgainInputRef} type="password" name="passwordAgain" className="form-control" id="passwordAgain" required value={passwordAgain} onChange={e => handleInputChange(e, passwordAgainInputRef)} />
                <div className="invalid-feedback">
                  {passwordAgainInputRef.current && passwordAgainInputRef.current.validationMessage !== 'Something went wrong' ? (passwordAgainInputRef.current.validationMessage.includes('Password') ? passwordAgainInputRef.current.validationMessage : 'Please confirm your password') : ''}
                </div>
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-7">
                <a href="/login">I already have a profile</a>
              </div>
              <div className="col-5 text-end">
                <button className="btn btn-primary">Sign Up</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SignUp;